// 22.01.19. Action creators. A way to create the real actions without making mistakes. These are used by the Redux reducers and Saga.
import { ActionTypes } from "../constants/actions";
import { ICase, IPo } from "src/constants/api";

// Sets `poNo` to undefined for POs that have `deleted` set to true
function fixDeletedPO(po: IPo): IPo {
  if (po.deleted) {
    return {
      ...po,
      poNo: undefined,
    };
  } else {
    return po;
  }
}

/* The updateCase action
   - Handled in:
     * reducers/home.tsx -- Sets the `caseSaveInProgress` boolean to display the loading indicator for the save action.
     * saga/detail.tsx   -- Sets the modifiedBy date and sends it to the API
   - Note: The API requires the poNo of POs to be deleted to be set to null. We map over all the POs and check a deleted flag,
           and set the poNo to undefined for those that are flagged for deletion.
*/
export const updateCase = (theCase: ICase, isReopen = false) => ({
  type: ActionTypes.DETAIL.UPDATE_CASE,
  payload: {
    ...theCase,
    pos: theCase.pos ? theCase.pos.map(fixDeletedPO) : undefined,
    isReopen,
  },
});

/* The selectCase action
   - Handled in:
     * saga/detail.tsx   -- The only thing this action does is navigate to /detail/{caseId}/mainform.
   - Note: This may not be needed at all if we can use `Link` somewhere instead.
*/
export const selectCase = (uniqueId: string) => ({
  type: ActionTypes.DETAIL.SELECT_CASE,
  payload: uniqueId,
});

/* The saveCase action
   - Handled in:
     * saga/detail.tsx   -- Send the case to the backend. 
     * reducers/home.tsx -- Sets the `caseSaveInProgress` flag to show loading indicator
   - Note: We also fix deleted POs here as in `updateCase` above, and when the action is created,
           it assigns a GUID to `uniqueId`
*/
export const saveCase = (theCase: ICase, uniqueId: string) => ({
  type: ActionTypes.DETAIL.SAVE_CASE,
  payload: {
    ...theCase,
    // uniqueId: Guid.newGuid(),
    uniqueId,
    pos: theCase.pos ? theCase.pos.map(fixDeletedPO) : undefined,
  },
});

/* The newCase action
   - Handled in:
     * saga/home.tsx   -- Sends an `initialize` message to redux-form with the appropriate data. 
   - Note: This is necessary because we need access to some state to be able to initialize a new case.
*/
export const newCase = () => ({
  type: ActionTypes.DETAIL.NEW_CASE,
});

/* The fetchCase action
   - Handled in:
     * saga/detail.tsx   -- Fetches a case from the server given a uniqueId 
*/
export const fetchCase = (uniqueId: string) => ({
  type: ActionTypes.DETAIL.FETCH_CASE,
  payload: uniqueId,
});

/* The fetchCaseSucceeded action
   - Handled in:
     * reducers/home.tsx   -- Updates the state with the new case. 
*/
export const fetchCaseSucceeded = (theCase: ICase) => ({
  type: ActionTypes.DETAIL.FETCH_CASE_SUCCEEDED,
  payload: theCase,
});

// Simple success/failure actions
export const saveCaseSucceeded = () => ({
  type: ActionTypes.DETAIL.SAVE_CASE_SUCCEEDED,
});

export const saveCaseFailed = (error: any) => ({
  type: ActionTypes.DETAIL.SAVE_CASE_FAILED,
  payload: error,
});

// Simply navigates home. This can probably be replaced by built-in actions from a library.
export const navigateHome = () => ({
  type: ActionTypes.DETAIL.NAVIGATE_HOME,
});

export const navigatePrint = () => ({
  type: ActionTypes.DETAIL.NAVIGATE_PRINT,
});

// All the actions below are launched when the initial data fetch is performed.
// The are all handled in the reducers in the same way -- their data is simply loaded into
// state in the appropriate place.
export const countriesReceived = (countries: any[]) => ({
  type: ActionTypes.DETAIL.COUNTRIES_RECEIVED,
  payload: countries,
});

export const issueTypesReceived = (issueTypes: any[]) => ({
  type: ActionTypes.DETAIL.ISSUE_TYPES_RECEIVED,
  payload: issueTypes,
});

export const claimTypesReceived = (claimTypes: any[]) => ({
  type: ActionTypes.DETAIL.CLAIM_TYPES_RECEIVED,
  payload: claimTypes,
});

export const grantTypesReceived = (grantTypes: any[]) => ({
  type: ActionTypes.DETAIL.GRANT_TYPES_RECEIVED,
  payload: grantTypes,
});

export const claimStatusReceived = (claimStatus: any[]) => ({
  type: ActionTypes.DETAIL.CLAIM_STATUS_RECEIVED,
  payload: claimStatus,
});

export const actionSaveAttachmentData = (
  file: any,
  uploadedBy: any,
  uniqueId: any,
  prevAttachmentData: any,
  type: string
) => ({
  payload: { uniqueId, file, uploadedBy, prevAttachmentData, type },
  type: ActionTypes.DETAIL.SAVE_ATTACHMENT_DATA,
});

export const actionDownloadAttachment = (fileId: any) => ({
  payload: { fileId },
  type: ActionTypes.DETAIL.DOWNLOAD_ATTACHMENT,
});

export const actionSetAttachmentData = (data: any) => ({
  type: ActionTypes.DETAIL.SET_ATTACHMENT_DATA,
  payload: { data },
});

export const actionSetAttachmentDataShipment = (data: any) => ({
  type: ActionTypes.DETAIL.SET_ATTACHMENT_DATA_SHIPMENT,
  payload: { data },
});

export const actionSetAttachmentDataShipmentInventory = (data: any) => ({
  type: ActionTypes.DETAIL.SET_ATTACHMENT_DATA_SHIPMENT_INVENTORY,
  payload: { data },
});

export const actionSetAttachmentDataQmu = (data: any) => ({
  type: ActionTypes.DETAIL.SET_ATTACHMENT_DATA_QMU,
  payload: { data },
});

export const actionSetAttachmentDataInventory = (data: any) => ({
  type: ActionTypes.DETAIL.SET_ATTACHMENT_DATA_INVENTORY,
  payload: { data },
});

export const actionSetAttachmentDataQmuOer = (data: any) => ({
  type: ActionTypes.DETAIL.SET_ATTACHMENT_DATA_QMU_OER,
  payload: { data },
});

export const actionDeleteAttachment = (
  fileId: any,
  attachmentData: any,
  type: string
) => ({
  type: ActionTypes.DETAIL.DELETE_ATTACHMENT,
  payload: { fileId, attachmentData, type },
});

export const allowEditCase = (allowEdit: any) => ({
  type: ActionTypes.DETAIL.ALLOW_EDIT,
  payload: allowEdit,
});

export const caseLockUnlock = (uniqueId: string, username: any) => ({
  type: ActionTypes.DETAIL.CASE_LOCK_UNLOCK,
  payload: {
    uniqueId,
    username,
  },
});

export const solutionUsersReceived = (solutionUsers: any[]) => ({
  type: ActionTypes.DETAIL.FETCH_SOLUTION_USERS_SUCCESS,
  payload: solutionUsers,
});

export const caseLockForEdit = (uniqueId: string, isEdit: boolean, updatedBy: string) => ({
  type: ActionTypes.DETAIL.CASE_LOCK_FOR_EDIT,
  payload: {
    uniqueId,
    isEdit,
    updatedBy,
  },
});

export const isLockForEdit = (payload: any) => ({
  type: ActionTypes.DETAIL.IS_LOCK_FOR_EDIT,
  payload,
});

export const requestForClose = (caseData: any, userDetails: any) => ({
  type: ActionTypes.DETAIL.REQUEST_FOR_CLOSE,
  payload: {
    caseData,
    userDetails,
  },
});

export const actionSetCurrentUsername = (payload: any) => ({
  type: ActionTypes.DETAIL.SET_CURRENT_USERNAME,
  payload,
});

export const actionIsCurrentUserSO = (payload: any) => ({
  type: ActionTypes.DETAIL.IS_CURRENT_USER_SO,
  payload,
});

export const actionIsCurrentUserST = (payload: any) => ({
  type: ActionTypes.DETAIL.IS_CURRENT_USER_ST,
  payload,
});

export const actionIsCheckOutByDiffUser = (payload: any) => ({
  type: ActionTypes.DETAIL.IS_CHECKOUT_BY_DIFF_USER,
  payload,
});

export const actionSaveSPUser = (payload: any) => ({
  type: ActionTypes.DETAIL.SAVE_SP_USER,
  payload,
});
